import type {
  EventJson,
  NotificationJson,
  NotificationPayloadJson,
} from '../gen/creativefabrica/notifications/v1/types_pb'

// oxlint-disable-next-line cf-web/no-barrel-file
export type { EventJson }
import { debug, log } from './common'
import { subscribe, unsubscribe } from './notifications-service'
import {
  connect,
  disconnect,
  isConnectedForToken,
  getErrorForToken,
  getReconnectAttemptsForToken,
  getStreamCredential,
  streamUsesCookieAuth,
} from './sse-connection'
import type { StreamKey } from './sse-connection'

export type NotificationCallback = (notification: NotificationPayloadJson) => void

type EventKey = string
type UnsubscribeCallback = () => void

interface EventEntry {
  event: EventJson
  subscriptionId: string | null
  callbacks: Set<NotificationCallback>
  subscribedCallbacks: Set<() => void>
  token: StreamKey
}

const SUBSCRIPTION_CANCEL_DELAY = 250
const UNSUBSCRIBE_DELAY = 1000

const eventEntries = new Map<EventKey, EventEntry>()
const unsubscribeTimeouts = new Map<EventKey, NodeJS.Timeout>()
const unsubscribeAbortControllers = new Map<EventKey, AbortController>()
const subscriptionCancelTimeouts = new Map<EventKey, NodeJS.Timeout>()

// Connection state - now managed per token
let lastEvent: NotificationJson | null = null

function getEventKey(event: EventJson, token: StreamKey): EventKey {
  return JSON.stringify({ event, token: token ?? 'cookie' })
}

/** On a cookie host every caller shares the cookie stream, whatever token it was holding. */
function toStreamKey(token: string | undefined): StreamKey {
  return streamUsesCookieAuth() ? undefined : token
}

export { lastEvent }

export function isConnected(token?: string): boolean {
  return isConnectedForToken(toStreamKey(token))
}

export function getError(token?: string): Event | null {
  return getErrorForToken(toStreamKey(token))
}

export function getReconnectAttempts(token?: string): number {
  return getReconnectAttemptsForToken(toStreamKey(token))
}

export function setNotificationsStoreState(
  state: Partial<{
    lastEvent: NotificationJson | null
  }>,
): void {
  if (state.lastEvent !== undefined) {
    lastEvent = state.lastEvent
  }
}

/**
 * Subscribes to an event and returns a function to unsubscribe from the event
 * @param event - The event to subscribe to
 * @param callback - The callback to call when a notification is received
 * @param token - Optional token for authentication
 * @returns A function to unsubscribe from the event
 */
export function subscribeToEvent(
  event: EventJson,
  callback: NotificationCallback,
  token: string | undefined,
  options?: {
    /** Called on acknowledgement and again after each reconnect. Events outside those stretches
     * are never delivered, so fetch missed state here. */
    onSubscribed?: () => void
  },
): UnsubscribeCallback {
  const streamKey = toStreamKey(token)
  const subscribeAbortController = new AbortController()
  connect(streamKey)

  const eventKey = getEventKey(event, streamKey)

  // Clear any existing timeout for the event
  clearPendingTimeouts(eventKey)

  const existingEntry = eventEntries.get(eventKey)
  if (existingEntry) {
    debug && log('Adding callback to existing event subscription', event)
    existingEntry.callbacks.add(callback)
  }
  const eventEntry =
    existingEntry ?? createEventEntry(event, callback, subscribeAbortController, streamKey)

  const onSubscribed = options?.onSubscribed
  if (onSubscribed) {
    eventEntry.subscribedCallbacks.add(onSubscribed)
    if (eventEntry.subscriptionId) {
      onSubscribed()
    }
  }

  return () => {
    if (onSubscribed) {
      eventEntries.get(eventKey)?.subscribedCallbacks.delete(onSubscribed)
    }
    // When the callback is unsubscribed, we remove the callback from the event
    unsubscribeCallback(eventKey, callback)
    const eventEntry = eventEntries.get(eventKey)
    if (eventEntry?.callbacks.size === 0) {
      subscriptionCancelTimeouts.set(
        eventKey,
        setTimeout(() => {
          subscribeAbortController.abort('CF_MANUAL_ABORT')
        }, SUBSCRIPTION_CANCEL_DELAY),
      )
    }
  }
}

function createEventEntry(
  event: EventJson,
  callback: NotificationCallback,
  subscribeAbortController: AbortController,
  token: StreamKey,
): EventEntry {
  const eventKey = getEventKey(event, token)
  const eventEntry: EventEntry = {
    event,
    subscriptionId: null,
    callbacks: new Set<NotificationCallback>([callback]),
    subscribedCallbacks: new Set<() => void>(),
    token,
  }

  eventEntries.set(eventKey, eventEntry)

  subscribe(event, subscribeAbortController, getStreamCredential(token))
    .then(response => {
      eventEntry.subscriptionId = response.subscriptions?.[0]?.id || null
      eventEntry.subscribedCallbacks.forEach(subscribedCallback => subscribedCallback())

      debug && log('Subscribed to event:', event, eventEntry.subscriptionId)
    })
    .catch(error => {
      debug && log('Failed to subscribe:', error)
      cleanupEventEntry(eventKey)
    })

  return eventEntry
}

function unsubscribeEvent(eventKey: EventKey) {
  const eventEntry = eventEntries.get(eventKey)
  if (!eventEntry) {
    // An entry is gone once its subscribe failed or its unsubscribe completed.
    debug && log(`Entry not found for ${eventKey} during unsubscribeEvent - likely cleared already`)
    return
  }

  scheduleUnsubscribe(eventKey)
}

/**
 * Unsubscribes a callback from an event, optionally unsubscribes from the event if there are no more callbacks.
 * @param eventKey - The key of the event
 * @param callback - The callback to unsubscribe
 * @returns void
 */
function unsubscribeCallback(eventKey: EventKey, callback: NotificationCallback) {
  const eventEntry = eventEntries.get(eventKey)
  if (!eventEntry) {
    debug &&
      log(`Entry not found for ${eventKey} during unsubscribeCallback - likely cleared already`)
    return
  }

  // 1. we remove the callback from the event
  eventEntry.callbacks.delete(callback)
  debug &&
    log(
      `Removed callback from event subscription ${eventKey}, ${eventEntry.callbacks.size} callbacks remain`,
    )

  // 2. if there are no more callbacks, we unsubscribe from the event
  if (eventEntry.callbacks.size === 0) {
    unsubscribeEvent(eventKey)
  }
}

function clearPendingTimeouts(eventKey: EventKey) {
  if (unsubscribeAbortControllers.get(eventKey)) {
    unsubscribeAbortControllers.get(eventKey)?.abort()
    unsubscribeAbortControllers.delete(eventKey)
  }
  if (unsubscribeTimeouts.get(eventKey)) {
    clearTimeout(unsubscribeTimeouts.get(eventKey))
    unsubscribeTimeouts.delete(eventKey)
  }
  if (subscriptionCancelTimeouts.get(eventKey)) {
    clearTimeout(subscriptionCancelTimeouts.get(eventKey))
    subscriptionCancelTimeouts.delete(eventKey)
  }
}

/**
 * Schedules an unsubscription from the backend service
 * @param eventKey - The key of the event
 * @returns void
 */
function scheduleUnsubscribe(eventKey: string) {
  const eventEntry = eventEntries.get(eventKey)

  if (!eventEntry?.subscriptionId) {
    return
  }

  const unsubscribeAbortController = new AbortController()
  unsubscribeAbortControllers.set(eventKey, unsubscribeAbortController)

  const subscriptionId = eventEntry.subscriptionId
  unsubscribeTimeouts.set(
    eventKey,
    setTimeout(() => {
      unsubscribe(
        [subscriptionId],
        unsubscribeAbortController,
        getStreamCredential(eventEntry.token),
      )
        .then(() => {
          debug && log('Unsubscribed from event:', subscriptionId)
          cleanupEventEntry(eventKey)
        })
        .catch(() => {
          debug && log('Failed to unsubscribe from event:', subscriptionId)
        })
        .finally(() => {
          // Clean up the timeout reference
          unsubscribeTimeouts.delete(eventKey)
        })
    }, UNSUBSCRIBE_DELAY),
  )
}

function cleanupEventEntry(eventKey: EventKey) {
  const eventEntry = eventEntries.get(eventKey)
  eventEntries.delete(eventKey)
  if (eventEntry && !hasSubscriptionsForToken(eventEntry.token)) {
    disconnect(eventEntry.token)
  }
}

export function routeNotificationToStore(notification: NotificationJson): void {
  lastEvent = notification

  // Find the event entry that matches this subscription ID
  const subscriptionId = notification.subscriptionId
  if (!subscriptionId || !notification.payload) {
    return
  }

  // oxlint-disable-next-line unicorn/prefer-spread
  const eventEntry = Array.from(eventEntries.values()).find(
    entry => entry.subscriptionId === subscriptionId,
  )

  if (eventEntry) {
    debug && log('Routing notification to event callbacks:', eventEntry.callbacks.size)

    eventEntry.callbacks.forEach((callback: NotificationCallback) => {
      callback(notification.payload!)
    })
  }
}

/** Subscriptions survive a reconnect — the backend keeps them per session — but events fired
 * while the stream was down were never delivered, so registered subscribers are told coverage
 * resumed and fetch what they missed. */
export function notifyStreamConnected(token: StreamKey): void {
  for (const eventEntry of eventEntries.values()) {
    if (eventEntry.token === token && eventEntry.subscriptionId) {
      eventEntry.subscribedCallbacks.forEach(subscribedCallback => subscribedCallback())
    }
  }
}

/** `undefined` asks about the cookie stream. */
export function hasSubscriptionsForToken(token?: StreamKey): boolean {
  // oxlint-disable-next-line unicorn/prefer-spread
  for (const entry of Array.from(eventEntries.values())) {
    if (entry.token === token) {
      return true
    }
  }
  return false
}
